import { sliderValuesHash } from '@/libs/components/src/lib/body/ccsQuestions';

import Combobox from '@/libs/components/src/lib/form/Combobox';
import { Input } from '@chiroup/components/form/Input';
import { Slider } from '@chiroup/components/form/Slider';
import { Textarea } from '@chiroup/components/form/Textarea';
import {
  CCSPrimaryQuestionIds,
  CCSSecondaryQuestionIds,
} from '@chiroup/core/constants/CCSQuestionIds';
import { QuestionAnswer } from '@chiroup/core/types/ComplaintSummary.type';
import React from 'react';
import HCCheckbox from './HCCheckbox';
import HCDate from './HCDate';
import HCRadioButton from './HCRadioButton';
import CheckboxWithInputs from './ProviderCheckbox';

interface EditingControlProps {
  questionId: string;
  questionData: QuestionAnswer;
  onAnswerChange: (
    questionId: string,
    newValue: string | string[],
    otherValue?: string,
    customInputData?: Record<string, string>
  ) => void;
}

const EditingControl: React.FC<EditingControlProps> = ({
  questionId,
  questionData,
  onAnswerChange,
}) => {
  const { questionType, options = [], selectedValues = [] } = questionData;
  const isProviderQuestion =
    questionId.includes(CCSPrimaryQuestionIds.providerQuestionId) ||
    questionId.includes(CCSSecondaryQuestionIds.providerQuestionId);
  const effectiveType = isProviderQuestion ? 'checkboxWithInputs' : questionType;

  if (isProviderQuestion && !(questionData as any).customInputData) {
    (questionData as any).customInputData = {};
  }

  //stripped is for question ids that have the complaint id tacked on to the end and needed for the slider values hash
  let strippedQuestionId;

  if (questionId.includes('interimSymptomsGeneralVAS')) {
    strippedQuestionId = 'interimSymptomsGeneralVAS';
  } else {
    // Extract UUID pattern if present (36 chars: 8-4-4-4-12)
    const uuidMatch = questionId.match(
      /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/
    );

    if (uuidMatch) {
      strippedQuestionId = uuidMatch[0];
    } else {
      // Fallback to first 36 chars for backward compatibility
      strippedQuestionId = questionId.slice(0, 36);
    }
  }

  switch (effectiveType) {
    case 'radio': {
      return (
        <HCRadioButton
          questionId={questionId}
          questionData={questionData}
          onAnswerChange={onAnswerChange}
        />
      );
    }
    case 'checkbox': {
      return (
        <HCCheckbox
          questionId={questionId}
          questionData={questionData}
          onAnswerChange={onAnswerChange}
        />
      );
    }
    case 'checkboxWithInputs': {
      return (
        <CheckboxWithInputs
          questionId={questionId}
          questionData={questionData}
          onAnswerChange={onAnswerChange}
        />
      );
    }
    case 'combobox': {
      return (
        <Combobox
          label={'NONE'}
          showLabel={false}
          name="aggravates"
          popoverClassName="z-[9999]"
          popoverSide="bottom"
          options={(options || [])?.map(opt => ({ text: opt.display, value: opt.id }))}
          onChange={(val: string) => {
            onAnswerChange(questionId, val);
          }}
          value={questionData.selectedValues}
        />
      );
    }
    case 'slider': {
      // Built-in CCS sliders resolve from sliderValuesHash (unchanged). Custom
      // survey sliders aren't in that hash, so fall back to the question's own
      // configured range/labels carried on questionData.sliderConfig.
      const hashCfg = sliderValuesHash?.[strippedQuestionId];
      const customCfg = hashCfg ? undefined : questionData.sliderConfig;

      return (
        <div className="w-full py-4">
          <Slider
            value={Number(selectedValues[0]) || 0}
            onChange={data => onAnswerChange(questionId, String(data.value || 0))}
            min={hashCfg?.min ?? customCfg?.min}
            max={hashCfg?.max ?? customCfg?.max}
            step={customCfg?.step ?? 1}
            left={hashCfg?.left ?? customCfg?.left}
            right={hashCfg?.right ?? customCfg?.right}
            valueSet={true}
            pain={false}
            showTopLabels={true}
          />
        </div>
      );
    }
    case 'date':
      return (
        <HCDate
          questionId={questionId}
          questionData={questionData}
          onAnswerChange={onAnswerChange}
        />
      );
    case 'textarea':
      return (
        <Textarea
          name={`edit-${questionId}`}
          rows={8}
          value={selectedValues[0] || ''}
          onChange={value => onAnswerChange(questionId, value)}
        />
      );
    default:
      // Heuristic: longer free-text fields like "details" or "describe"
      if (/details|describe|notes|explain/i.test(questionData?.questionText || '')) {
        return (
          <Textarea
            name={`edit-${questionId}`}
            rows={6}
            value={selectedValues[0] || ''}
            onChange={value => onAnswerChange(questionId, value)}
          />
        );
      }

      return (
        <Input
          name={`edit-${questionId}`}
          type="text"
          value={selectedValues[0] || ''}
          onChange={value => onAnswerChange(questionId, value)}
          className="w-48 primary"
        />
      );
  }
};

export default EditingControl;
